/**
 * App Component with Routing
 */
import React, { useEffect, useLayoutEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { tokenService } from "./services/ServiceInstances";
import { SESSION_STORAGE_KEYS } from "./services/TokenService";
import {
  PAYMENTAPPURL,
  QUICKPAYURL,
  BOLTMYACCOUNTSURL,
  BASEURL,
  AUTHURL,
  AUTH_ENV_NAME,
  AUTH_API_KEY,
  AUTH_GOOGLE_CLIENT_ID,
  AUTH_TRUECALLER_APP_KEY,
  AUTH_FINORAMIC_CLIENT_ID,
  AUTH_WEBENGAGE_KEY,
  AUTH_MOENGAGE_KEY,
  AUTH_ENABLE_GOOGLE,
  AUTH_ENABLE_TRUECALLER,
  AUTH_ENABLE_FINORAMIC,
  AUTH_ENABLE_ANALYTICS,
  AUTH_ENABLE_DEBUG,
} from "../environments/environment";
import { Token } from "./after-login/token/token";
import { parseAuthTokenParamString } from "./after-login/base-redirect/redirect-helpers";

// Import route configurations
import { AfterLoginRoutes } from "./routes/after-login.routes";
import { StyleguideRoutes } from "./routes/styleguide.routes";

// Import context providers
import { AppProvider } from "./contexts/AppProvider";

// Import analytics
import funnelTrackingService from "./services/analytics/FunnelTrackingService";
import webEngageService from "./services/analytics/WebEngageService";
import googleAnalyticsService from "./services/analytics/GoogleAnalyticsService";
import { decryptToken } from "../utils/tokenEncryption";
import { DMI_THEME_CLASS, isDmiZestRoute } from "./dmi-zest-dashboard/dmiZestTheme";
// ZestAuth SDK is lazy-loaded via ZestAuthWrapper so that its CSS
// (which contains broad selectors like #root, .input-wrapper, .txt, .modal-container)
// only loads when the user visits an auth route — not on dashboard pages.
const ZestAuthLazy = React.lazy(() => import("./components/ZestAuthWrapper"));
const DmiZestDashboard = React.lazy(
  () => import("./dmi-zest-dashboard/DmiZestDashboard"),
);

declare var window: any;

// SDK auth routes — ZestAuth is only rendered on these paths
const SDK_AUTH_PATHS = [
  "/authentication",
  "/email",
  "/forgot-password",
  "/logout",
];

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // Only mount the SDK on auth-related routes to prevent CSS pollution on dashboard.
  // The "/" path redirects to /authentication via the catch-all <Route>, so no need to
  // check for it here.
  const isAuthRoute = SDK_AUTH_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );

  // Apply DMI dark theme before the lazy dashboard chunk loads (Suspense #loading_spin)
  useLayoutEffect(() => {
    if (!isDmiZestRoute(location.pathname)) {
      return;
    }

    document.documentElement.classList.add(DMI_THEME_CLASS);
    document.body.classList.add(DMI_THEME_CLASS);

    return () => {
      document.documentElement.classList.remove(DMI_THEME_CLASS);
      document.body.classList.remove(DMI_THEME_CLASS);
    };
  }, [location.pathname]);

  // Redirect to /authentication when not logged in (no token), except on /email
  // when ngx-webstorage|zest-authentication exists in sessionStorage.
  const pathsAllowedWithoutToken = [
    "/authentication",
    "/forgot-password",
    "/logout",
    "/styleguide",
    "/dmi-zest-dashboard",
  ];
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const hashCleaned = hash.replace(/^[#?]+/, "").replace(/\+/g, "%2B");
      const hashParams = new URLSearchParams(hashCleaned);
      const tokenFromHash = parseAuthTokenParamString(hash);
      console.log("tokenFromHash", tokenFromHash);
      const postLoginRedirect = hashParams.get("post_login_redirect_url") || "";

      if (
        tokenFromHash.access_token &&
        postLoginRedirect === "/dashboard/profile"
      ) {
        tokenService.setAppMandateRedirect();
      }

      // OAuth implicit / hash callback: persist before auth guard — pathname has no token,
      // and dashboard children (useRedirectLogic) never mount if we redirect first.
      if (
        tokenFromHash.access_token &&
        tokenFromHash.token_type === "Bearer" &&
        tokenFromHash.expires_in === 3600
      ) {
        tokenService.saveToSession({
          access_token: tokenFromHash.access_token,
          expires_in: String(tokenFromHash.expires_in),
          token_type: tokenFromHash.token_type,
        });
        tokenService.setWebEngageUserId(tokenFromHash.access_token);
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
    }

    const path = location.pathname;
    if (pathsAllowedWithoutToken.some((p) => path.startsWith(p))) return;

    const hasToken = !!tokenService.getToken();
    if (hasToken) return;

    if (path.startsWith("/email")) {
      const zestAuth = sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTHENTICATION);
      if (zestAuth != null && zestAuth !== "") return;
    }

    navigate("/authentication", { replace: true });
  }, [location.pathname, navigate]);

  // SDK injects TWO <style> tags:
  //   1. Tailwind CSS (~150KB) — contains "--tw-translate-x" / "@layer properties"
  //   2. Auth-specific CSS (~80KB) — contains ".auth-v3-contianer"
  // Both must be toggled to prevent CSS pollution on dashboard.
  const SDK_MARKERS = [".auth-v3-contianer", "--tw-translate-x"];
  const isSdkStyle = (s: HTMLStyleElement) =>
    s.textContent != null &&
    SDK_MARKERS.some((m) => s.textContent!.includes(m));

  // Toggle SDK stylesheets: use media="not all" to deactivate on non-auth routes.
  useLayoutEffect(() => {
    const sdkStyles = Array.from(document.querySelectorAll("style")).filter(
      isSdkStyle,
    );
    const targetMedia = isAuthRoute ? "all" : "not all";
    sdkStyles.forEach((s) => {
      s.media = targetMedia;
    });

    // Watch for SDK style tags injected AFTER our toggle (lazy loading)
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (
            node.nodeName === "STYLE" &&
            isSdkStyle(node as HTMLStyleElement)
          ) {
            (node as HTMLStyleElement).media = isAuthRoute ? "all" : "not all";
          }
        }
      }
    });
    observer.observe(document.head, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [isAuthRoute, location.pathname]);

  useEffect(() => {
    // Initialize analytics services
    funnelTrackingService.initialize();
    googleAnalyticsService.initialize();

    // Set up message receiver for cross-app communication
    receiveMsgFromPaymentApp();

    // Parse URL parameters for various contexts
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("boltBills")) {
      tokenService.setBoltContext();
    }
    if (urlParams.get("gcRedirect")) {
      tokenService.setGCRedirectContext();
    }
    if (urlParams.get("url_post_login")) {
      tokenService.setMyAccountsV2UrlRoute(
        urlParams.get("url_post_login") || "",
      );
    }

    // Set UTM parameters for tracking
    if (
      urlParams.get("utm_source") &&
      urlParams.get("utm_medium") &&
      urlParams.get("utm_campaign")
    ) {
      tokenService.updateAndSetGlobalParams(
        "Utm_Source",
        urlParams.get("utm_source") || "",
      );
      tokenService.updateAndSetGlobalParams(
        "Utm_Medium",
        urlParams.get("utm_medium") || "",
      );
      tokenService.updateAndSetGlobalParams(
        "Utm_Campaign",
        urlParams.get("utm_campaign") || "",
      );
    }

    // Storyly context
    if (window.location.search.includes("storyly")) {
      tokenService.setStorylyContext(window.location.search);
    }

    // Set up WebEngage user if authenticated
    const customerId = tokenService.getCustomerId();
    if (customerId) {
      webEngageService.login(customerId);
      googleAnalyticsService.setUserId(customerId);
    }
  }, [tokenService]);

  const receiveMsgFromPaymentApp = () => {
    const messageHandler = (e: MessageEvent) => {
      const allowedOrigins = [PAYMENTAPPURL, BOLTMYACCOUNTSURL];

      if (e.origin === QUICKPAYURL) {
        tokenService.receiveTokenAcknowledgementFromrepay(e.data);
      }

      if (!allowedOrigins.includes(e.origin)) {
        return;
      }

      const action = e.data;
      if (action === "tokenReceived") {
        tokenService.receiveTokenAcknowledgementFromPaymentApp(action);
      } else if (action === "applicationReceived") {
        tokenService.receiveApplicationAcknowledgementFromPaymentApp(action);
      } else if (action === "myaccountsTokenReceived") {
        tokenService.receiveApplicationAcknowledgementFromMyAccountsV2(e.data);
      }
    };

    window.addEventListener("message", messageHandler, false);

    // Return cleanup function
    return () => {
      window.removeEventListener("message", messageHandler, false);
    };
  };

  /**
   * Handle successful authentication from the ZestAuth SDK.
   * Replicates the logic from the former Authentication.tsx handleSuccessfulLogin:
   * 1. Save token to session storage
   * 2. Set up analytics (WebEngage, Google Analytics, funnel tracking)
   * 3. Navigate to the appropriate post-login destination
   */
  const handleToken = useCallback(
    (sdkToken: any) => {
      const token: Token = {
        access_token: sdkToken.access_token,
        token_type: sdkToken.token_type,
        expires_in: sdkToken.expires_in,
      };

      tokenService.saveToSession(token);
      tokenService.setWebEngageUserId(token.access_token || "");

      funnelTrackingService.funnelTrack(
        "auth",
        "login_success",
        "event",
        "success",
      );

      const customerId = tokenService.getCustomerId();
      if (customerId) {
        webEngageService.login(customerId);
        googleAnalyticsService.setUserId(customerId);
      }

      // Navigate based on context (bolt, GC redirect, or dashboard)
      if (tokenService.getBoltContext()) {
        handleBoltRedirection();
      } else if (tokenService.getGCRedirectContext()) {
        handleGCRedirection();
      } else {
        const postLoginRedirect =
          tokenService.getPostLoginRedirectComponentUrl();
        if (postLoginRedirect) {
          navigate(postLoginRedirect);
          tokenService.removePostLoginRedirectComponentUrl();
        } else {
          navigate("/dashboard");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  const handleBoltRedirection = () => {
    const token = tokenService.getToken();
    if (token) {
      tokenService.sendDataToMyAccountsV2("myaccountsToken", token);
      let intervalCounter = 0;
      const interval = setInterval(() => {
        const ack = tokenService.checkTokenAcknowledgementFromMyAccountsV2();
        if (ack) {
          clearInterval(interval);
          const route = tokenService.getMyAccountsV2UrlRoute();
          window.location.href = route
            ? `${BOLTMYACCOUNTSURL}/${route}`
            : BOLTMYACCOUNTSURL;
        } else {
          intervalCounter++;
        }
        if (intervalCounter > 100) {
          clearInterval(interval);
          navigate("/dashboard");
        }
      }, 100);
    }
  };

  const handleGCRedirection = () => {
    const customerId = tokenService.getCustomerId();
    const redirectUrl = `${BASEURL}/emi/get?utm_source=myaccounts&utm_medium=shop&utm_campaign=organic&ue=${btoa(customerId || "")}&ew=50000&va=50000&ut=${JSON.stringify(tokenService.getAccessToken())}`;
    window.location.href = redirectUrl;
  };

  const config = {
    name: AUTH_ENV_NAME,
    authBaseUrl: AUTHURL,
    apiBaseUrl: AUTHURL,
    apiKey: AUTH_API_KEY,
    googleClientId: AUTH_GOOGLE_CLIENT_ID,
    truecallerAppKey: AUTH_TRUECALLER_APP_KEY,
    finoramicClientId: AUTH_FINORAMIC_CLIENT_ID,
    webEngageKey: AUTH_WEBENGAGE_KEY,
    moEngageKey: AUTH_MOENGAGE_KEY,
    features: {
      enableGoogleAuth: AUTH_ENABLE_GOOGLE,
      enableTruecallerAuth: AUTH_ENABLE_TRUECALLER,
      enableFinoramicAuth: AUTH_ENABLE_FINORAMIC,
      enableAnalytics: AUTH_ENABLE_ANALYTICS,
      enableDebugMode: AUTH_ENABLE_DEBUG,
    },
  };

  return (
    <>
      {/* Suspense fallback: render the same #loading_spin as index.html
           so it seamlessly continues the initial Zest logo spinner.
           On auth routes the SDK has its own identical Zest spinner, so we
           skip the host spinner to avoid showing two at once. */}
      <React.Suspense fallback={isAuthRoute ? null : <div id="loading_spin"></div>}>
        {/* Only mount ZestAuth on auth routes — SDK provides its own loader */}
        {isAuthRoute && (
          <React.Suspense fallback={null}>
            <ZestAuthLazy config={config} onToken={handleToken} />
          </React.Suspense>
        )}

        <Routes>
          <Route path="/dmi-zest-dashboard" element={<DmiZestDashboard />} />
          {AfterLoginRoutes}
          {StyleguideRoutes}
        </Routes>
      </React.Suspense>

      {/* IFrames for cross-app communication */}
      <iframe
        id="receiver"
        src={`${PAYMENTAPPURL}/iframe`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="Payment App Receiver"
      />
      <iframe
        id="repayreceiver"
        src={`${QUICKPAYURL}/iframe`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="QuickPay Receiver"
      />
      <iframe
        id="myaccountsV2"
        src={`${BOLTMYACCOUNTSURL}/iframe`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="MyAccounts V2 Receiver"
      />
    </>
  );
}

function App() {

  // Read encrypted token from URL query (?access_token=<encryptedToken>) and decrypt it
  useEffect(() => {
    extractTokenFromUrl();
  }, []);

  const extractTokenFromUrl = async () => {
    // Extract access_token from query string exactly as in the URL (no decode/parse)
    const search = window.location.search;
    const prefix = "access_token=";
    const start = search.indexOf(prefix);
    if (start === -1) return;
    const valueStart = start + prefix.length;
    const nextAmp = search.indexOf("&", valueStart);
    const encryptedToken =
      nextAmp === -1 ? search.slice(valueStart) : search.slice(valueStart, nextAmp);
    if (!encryptedToken) return;

    try {
      const decryptedToken = await decryptToken(encryptedToken);
      const token = JSON.parse(decryptedToken) as Token;
   
      if (token?.access_token) {
        tokenService.saveToSession(token);
        window.history.replaceState({}, "", window.location.pathname);
        window.location.replace("/dashboard");
      }
    } catch (err) {
      console.warn("Failed to extract or decrypt token from URL:", err);
    }
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </Router>
  );
}

export default App;
